<template>
    <v-container class="py-7">
        <v-row>
            <v-col cols="12">
                <div class="title-container">
                    <div class="title-block">
                        <span class="page-title">Gráficos por Classe</span>
                        <v-breadcrumbs class="app-breadcrumb" divider="›" :items="breadcrumbItems"></v-breadcrumbs>
                    </div>
                    <div class="button-group chart-actions">
                        <v-tooltip bottom>
                            <template #activator="{ on, attrs }">
                                <v-btn type="button" elevation="0" color="primary" outlined
                                    @click="generateVotingPdf" :disabled="generatingPdf" :loading="generatingPdf"
                                    v-on="on" v-bind="attrs">
                                    <v-icon left v-if="!generatingPdf">mdi-file-pdf-box</v-icon>
                                    Gerar PDF
                                </v-btn>
                            </template>
                            <span>Gerar laudo das adesões em PDF</span>
                        </v-tooltip>

                        <v-tooltip bottom>
                            <template #activator="{ on, attrs }">
                                <v-btn type="button" elevation="0" color="primary" outlined to="/adesao-plano"
                                    v-on="on" v-bind="attrs">
                                    <v-icon left>mdi-arrow-left</v-icon>
                                    Voltar
                                </v-btn>
                            </template>
                            <span>Voltar para a listagem de adesões ao plano</span>
                        </v-tooltip>
                    </div>
                </div>
            </v-col>
        </v-row>

        <v-row v-if="loading" class="mt-6">
            <v-col cols="12" class="text-center">
                <v-progress-circular indeterminate color="primary" size="64" />
                <p class="mt-3 grey--text">Carregando dados dos gráficos...</p>
            </v-col>
        </v-row>

        <template v-if="!loading">
            <v-row v-for="card in classCards" :key="card.key" class="mt-4">
                <v-col cols="12">
                    <v-card class="chart-card">
                        <v-card-title class="chart-card-title">
                            <v-icon left :color="card.color">{{ card.icon }}</v-icon>
                            {{ card.title }}
                            <v-spacer />
                            <v-chip small :color="card.color" text-color="white" class="ml-2">
                                {{ formatNumber(chartData[card.key].adherenceCount) }} / {{ formatNumber(chartData[card.key].totalCount) }} credores
                            </v-chip>
                            <v-chip v-for="currency in currencyOptions" :key="card.key + '-' + currency.key + '-chip'"
                                small :color="card.color" text-color="white" class="ml-2">
                                {{ formatCurrency(chartData[card.key].currencies[currency.key].adherenceAmount, currency.key) }}
                                /
                                {{ formatCurrency(chartData[card.key].currencies[currency.key].totalAmount, currency.key) }}
                            </v-chip>
                        </v-card-title>

                        <v-card-text>
                            <v-row>
                                <v-col cols="12" md="3">
                                    <div class="chart-subtitle">Proporção por Cabeça (quantidade de credores)</div>
                                    <div class="donut-shell">
                                        <div class="donut-chart" :style="donutStyle(
                                            chartData[card.key].adherenceCount,
                                            chartData[card.key].totalCount,
                                            card.color,
                                            card.secondaryColor
                                        )">
                                            <div class="donut-center">
                                                <span>Cabeça</span>
                                                <strong>{{ formatNumber(chartData[card.key].adherenceCount) }} de {{ formatNumber(chartData[card.key].totalCount) }}</strong>
                                            </div>
                                        </div>
                                        <div class="chart-legend">
                                            <div class="legend-item">
                                                <span class="legend-dot" :style="{ backgroundColor: card.color }"></span>
                                                <span>Aderiram ({{ percentage(chartData[card.key].adherenceCount, chartData[card.key].totalCount) }}%)</span>
                                            </div>
                                            <div class="legend-item">
                                                <span class="legend-dot" :style="{ backgroundColor: card.secondaryColor }"></span>
                                                <span>Não manifestaram ({{ percentage(notAdheredCount(chartData[card.key]), chartData[card.key].totalCount) }}%)</span>
                                            </div>
                                        </div>
                                    </div>
                                </v-col>

                                <v-col v-for="currency in currencyOptions" :key="card.key + '-' + currency.key + '-amount'" cols="12" md="3">
                                    <div class="chart-subtitle">Proporção por Valor ({{ currency.label }})</div>
                                    <div class="donut-shell">
                                        <div class="donut-chart" :style="donutStyle(
                                            chartData[card.key].currencies[currency.key].adherenceAmount,
                                            chartData[card.key].currencies[currency.key].totalAmount,
                                            card.color,
                                            card.secondaryColor
                                        )">
                                            <div class="donut-center">
                                                <span>{{ currency.label }}</span>
                                                <strong>{{ formatCurrency(chartData[card.key].currencies[currency.key].adherenceAmount, currency.key) }}</strong>
                                            </div>
                                        </div>
                                        <div class="chart-legend">
                                            <div class="legend-item">
                                                <span class="legend-dot" :style="{ backgroundColor: card.color }"></span>
                                                <span>Aderido ({{ percentage(chartData[card.key].currencies[currency.key].adherenceAmount, chartData[card.key].currencies[currency.key].totalAmount) }}%)</span>
                                            </div>
                                            <div class="legend-item">
                                                <span class="legend-dot" :style="{ backgroundColor: card.secondaryColor }"></span>
                                                <span>Não manifestaram ({{ percentage(notAdheredAmount(chartData[card.key].currencies[currency.key]), chartData[card.key].currencies[currency.key].totalAmount) }}%)</span>
                                            </div>
                                        </div>
                                    </div>
                                </v-col>
                            </v-row>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>

            <v-row v-if="noData">
                <v-col cols="12">
                    <v-alert type="info" text class="mt-4">
                        Nenhuma informação encontrada.
                    </v-alert>
                </v-col>
            </v-row>
        </template>
    </v-container>
</template>

<script>
import axios from 'axios';

function createEmptyCurrencies() {
    return {
        brl: { totalCount: 0, totalAmount: 0, adherenceCount: 0, adherenceAmount: 0 },
        usd: { totalCount: 0, totalAmount: 0, adherenceCount: 0, adherenceAmount: 0 },
        eur: { totalCount: 0, totalAmount: 0, adherenceCount: 0, adherenceAmount: 0 },
    };
}

export default {
    name: 'PlanAdherenceCharts',

    data: () => ({
        loading: false,
        generatingPdf: false,
        breadcrumbItems: [
            { text: 'Homepage', disabled: false, to: '/home', exact: true },
            { text: 'Adesão ao Plano', disabled: false, to: '/adesao-plano', exact: true },
            { text: 'Gráficos por Classe', disabled: true, exact: true },
        ],
        currencyOptions: [
            { key: 'brl', label: 'BRL', symbol: 'R$' },
            { key: 'usd', label: 'USD', symbol: 'US$' },
            { key: 'eur', label: 'EUR', symbol: '€' },
        ],
        classCards: [
            { key: 'trabalhista', title: 'Classe I — Trabalhista', icon: 'mdi-briefcase-outline', color: '#7E55AB', secondaryColor: '#C4A4D8' },
            { key: 'quirografarios', title: 'Classe III — Quirografários', icon: 'mdi-account-group-outline', color: '#1E88E5', secondaryColor: '#90CAF9' },
            { key: 'meEpp', title: 'Classe IV — ME/EPP', icon: 'mdi-store-outline', color: '#00796B', secondaryColor: '#80CBC4' },
        ],
        chartData: {
            trabalhista: { totalCount: 0, totalAmount: 0, adherenceCount: 0, adherenceAmount: 0, currencies: createEmptyCurrencies() },
            quirografarios: { totalCount: 0, totalAmount: 0, adherenceCount: 0, adherenceAmount: 0, currencies: createEmptyCurrencies() },
            meEpp: { totalCount: 0, totalAmount: 0, adherenceCount: 0, adherenceAmount: 0, currencies: createEmptyCurrencies() },
        },
    }),

    computed: {
        noData() {
            return this.classCards.every(card => this.chartData[card.key].totalCount === 0);
        },
    },

    mounted() {
        this.loadCharts();
    },

    methods: {
        async loadCharts() {
            try {
                this.loading = true;
                const result = await axios.get('/api/admin/plan-adherence/user-creditors-charts');
                const data = result.data.content || {};

                this.chartData = {
                    trabalhista: this.normalizeClassData(data.trabalhista),
                    quirografarios: this.normalizeClassData(data.quirografarios),
                    meEpp: this.normalizeClassData(data.me_epp),
                };
            } catch (error) {
                this.$dialog.notify.error('Erro ao carregar gráficos de adesão.', {
                    position: 'top-right',
                    timeout: 3000,
                });
            } finally {
                this.loading = false;
            }
        },

        async generateVotingPdf() {
            try {
                const proceed = await this.$dialog.confirm({
                    text: 'Deseja gerar o laudo das adesões em PDF?',
                    title: 'Gerar laudo em PDF',
                    actions: {
                        false: 'Cancelar',
                        true: 'Gerar',
                    },
                });

                if (!proceed) return;

                this.generatingPdf = true;
                const response = await axios.post('/api/admin/plan-adherence/voting-report-pdf', {}, {
                    responseType: 'blob',
                });

                const blob = new Blob([response.data], { type: 'application/pdf' });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = 'laudo_adesoes.pdf';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);

                this.$dialog.notify.success('Laudo gerado com sucesso!', {
                    position: 'top-right',
                    timeout: 2000,
                });
            } catch (error) {
                this.$dialog.notify.error('Erro ao gerar laudo em PDF.', {
                    position: 'top-right',
                    timeout: 3000,
                });
            } finally {
                this.generatingPdf = false;
            }
        },

        normalizeClassData(source) {
            const data = source || {};
            return {
                totalCount: Number(data.total_count) || 0,
                totalAmount: Number(data.total_amount) || 0,
                adherenceCount: Number(data.adherence_count) || 0,
                adherenceAmount: Number(data.adherence_amount) || 0,
                currencies: this.normalizeCurrencyData(data.currencies),
            };
        },

        normalizeCurrencyData(source) {
            const normalized = createEmptyCurrencies();

            this.currencyOptions.forEach(currency => {
                const item = source && source[currency.key] ? source[currency.key] : {};
                normalized[currency.key] = {
                    totalCount: Number(item.total_count) || 0,
                    totalAmount: Number(item.total_amount) || 0,
                    adherenceCount: Number(item.adherence_count) || 0,
                    adherenceAmount: Number(item.adherence_amount) || 0,
                };
            });

            return normalized;
        },

        formatNumber(value) {
            return Number(value || 0).toLocaleString('pt-BR');
        },

        formatCurrency(value, currencyKey) {
            const currency = this.currencyOptions.find(item => item.key === currencyKey) || this.currencyOptions[0];
            const formattedValue = Number(value || 0)
                .toFixed(2)
                .replace('.', ',')
                .replace(/\B(?=(\d{3})+(?!\d))/g, '.');

            return `${currency.symbol} ${formattedValue}`;
        },

        percentage(part, total) {
            const totalValue = Number(total) || 0;
            if (totalValue <= 0) return '0.0';
            return ((Number(part || 0) / totalValue) * 100).toFixed(1);
        },

        donutStyle(part, total, primaryColor, secondaryColor) {
            const totalValue = Number(total) || 0;
            const rawPercent = totalValue > 0 ? (Number(part || 0) / totalValue) * 100 : 0;
            const percent = Math.max(0, Math.min(100, rawPercent));

            return {
                background: `conic-gradient(${primaryColor} 0 ${percent}%, ${secondaryColor} ${percent}% 100%)`,
            };
        },

        notAdheredCount(classData) {
            return Math.max(0, Number(classData.totalCount || 0) - Number(classData.adherenceCount || 0));
        },

        notAdheredAmount(currencyData) {
            return Math.max(0, Number(currencyData.totalAmount || 0) - Number(currencyData.adherenceAmount || 0));
        },
    },
};
</script>

<style scoped>
.chart-actions {
    display: flex;
    align-items: center;
    gap: 12px;
    padding-right: 16px;
}

.chart-card {
    border-radius: 8px;
}

.chart-card-title {
    flex-wrap: wrap;
    gap: 8px 0;
}

.chart-subtitle {
    min-height: 44px;
    margin-bottom: 12px;
    color: #757575;
    font-size: 15px;
    font-weight: 700;
    text-align: center;
}

.donut-shell {
    display: flex;
    flex-direction: column;
    align-items: center;
}

.donut-chart {
    position: relative;
    width: min(260px, 100%);
    aspect-ratio: 1;
    border-radius: 50%;
}

.donut-chart::after {
    content: '';
    position: absolute;
    inset: 22%;
    border-radius: 50%;
    background: #ffffff;
}

.donut-center {
    position: absolute;
    inset: 28%;
    z-index: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
}

.donut-center span {
    color: #555555;
    font-size: 14px;
}

.donut-center strong {
    margin-top: 6px;
    color: #333333;
    font-size: 15px;
}

.chart-legend {
    display: flex;
    flex-direction: column;
    gap: 6px;
    width: 100%;
    max-width: 280px;
    margin-top: 14px;
}

.legend-item {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #333333;
    font-size: 13px;
    font-weight: 600;
}

.legend-dot {
    width: 12px;
    height: 12px;
    flex: 0 0 12px;
    border-radius: 50%;
}
</style>
